import { CommonButton, CommonLink } from 'components/common/Forms/Button';
import { Loading } from 'components/common/Loading';
import { useCommonTranslation } from 'hooks/i18n/useCommonTranslation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { patientURL } from 'routes/AppRoutes';

import { useSelfCheckIn } from 'hooks/patient/checkIn/useSelfCheckIn';
import { MILLIS_IN_MINUTE } from 'utils/dateUtil';
import {
  CheckInNumberInput,
  FormButtonBar,
  FormInputBar,
  InputLabel,
  PatientCheckInFormStyled,
  PatientCheckInWrapperStyled,
} from './PatientCheckIn.styled';
import { PatientCheckInLayout } from './PatientCheckInLayout';

type PatientCheckInFormValues = {
  code: number;
};

export const PatientCheckInCodeInput = () => {
  const { t } = useCommonTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<PatientCheckInFormValues>();
  const [displayInvalidCode, setDisplayInvalidCode] = useState(false);
  const { isLoading, checkInWithCode } = useSelfCheckIn({
    onInvalidCode: () => setDisplayInvalidCode(true),
  });

  useEffect(() => {
    if (!displayInvalidCode) {
      return;
    }
    const timeout = setTimeout(
      () => setDisplayInvalidCode(false),
      MILLIS_IN_MINUTE,
    );
    return () => clearTimeout(timeout);
  }, [displayInvalidCode]);

  if (displayInvalidCode) {
    return (
      <PatientCheckInLayout
        label={t('self-checkin-invalid-code-title')}
        description={{ html: t('self-checkin-invalid-code') }}
      >
        <PatientCheckInWrapperStyled>
          <FormButtonBar>
            <CommonButton
              variant="primary"
              size="large"
              onClick={() => setDisplayInvalidCode(false)}
            >
              {t('ok')}
            </CommonButton>
          </FormButtonBar>
        </PatientCheckInWrapperStyled>
      </PatientCheckInLayout>
    );
  }

  return (
    <PatientCheckInLayout
      description={{
        html: (
          <>
            {t('self-checkin-code-description-line-1')}
            <br />
            {t('self-checkin-code-description-line-2')}
          </>
        ),
        compact: true,
      }}
    >
      {isLoading && <Loading />}
      <PatientCheckInFormStyled
        onSubmit={handleSubmit(({ code }) => checkInWithCode(code))}
      >
        <FormInputBar>
          <InputLabel htmlFor="code">
            {t('patient-check-in-input-label')}
          </InputLabel>
          <CheckInNumberInput
            id="code"
            {...register('code', {
              required: t('patient-check-in-enter-valid-code') as string,
              minLength: {
                value: 4,
                message: t('patient-check-in-enter-valid-code'),
              },
              maxLength: {
                value: 4,
                message: t('patient-check-in-enter-valid-code'),
              },
              pattern: {
                value: /^\d\d\d\d$/,
                message: t('patient-check-in-enter-valid-code'),
              },
            })}
            type="number"
            hasError={!!errors.code?.message}
            helperText={errors.code?.message}
            noSpaceForHelperText
          />
        </FormInputBar>
        <FormButtonBar>
          <CommonButton variant="primary" type="submit" size="large">
            {t('self-checkin-submit')}
          </CommonButton>
          <CommonLink variant="secondary" size="large" to={patientURL}>
            {t('back-to-menu')}
          </CommonLink>
        </FormButtonBar>
      </PatientCheckInFormStyled>
    </PatientCheckInLayout>
  );
};
